const { mainConfigService } = require('./services/config');
const { version } = require('../../package.json');
const Sentry = require('@sentry/browser');
const { fetchSentryConfig } = require('./http');
const { isUrl } = require('./util');
const { CaptureConsole, ContextLines, Debug, ExtraErrorData, HttpClient, ReportingObserver, RewriteFrames } = require('@sentry/integrations');
const { sentryIgnoreErrors } = require('./fields');
const { SENSITIVE_FIELDS, SENSITIVE_URL_PATTERNS } = require('./constant/sentry');

const createIntegration = (integrationName, integrationConfig) => {
  let integration = null;
  switch (integrationName) {
  case 'Replay':
    integration = new Sentry.Replay(integrationConfig);
    break;
  case 'BrowserTracing':
    integration = new Sentry.BrowserTracing(integrationConfig);
    break;
  case 'Breadcrumbs':
    integration = new Sentry.Integrations.Breadcrumbs(integrationConfig);
    break;
  case 'Dedupe':
    integration = new Sentry.Integrations.Dedupe(integrationConfig);
    break;
  case 'GlobalHandlers':
    integration = new Sentry.Integrations.GlobalHandlers(integrationConfig);
    break;
  case 'InboundFilters':
    integration = new Sentry.Integrations.InboundFilters(integrationConfig);
    break;
  case 'LinkedErrors':
    integration = new Sentry.Integrations.LinkedErrors(integrationConfig);
    break;
  case 'TryCatch':
    integration = new Sentry.Integrations.TryCatch(integrationConfig);
    break;
  case 'HttpContext':
    integration = new Sentry.Integrations.HttpContext(integrationConfig);
    break;
  case 'ModuleMetadata':
    integration = new Sentry.ModuleMetadata(integrationConfig);
    break;
  case 'CaptureConsole':
    integration = new CaptureConsole(integrationConfig);
    break;
  case 'ContextLines':
    integration = new ContextLines(integrationConfig);
    break;
  case 'Debug':
    integration = new Debug(integrationConfig);
    break;
  case 'HttpClient':
    integration = new HttpClient(integrationConfig);
    break;
  case 'ExtraErrorData':
    integration = new ExtraErrorData(integrationConfig);
    break;
  case 'ReportingObserver':
    integration = new ReportingObserver(integrationConfig);
    break;
  case 'RewriteFrames':
    integration = new RewriteFrames(integrationConfig);
    break;
  default:
    console.log({'status':'error', 'message':'Invalid Sentry Configs'});
  }

  return integration;
};

const createSentryConfig = (sentryConfig,  merchant_id) => {
  const integrations = [];

  if (sentryConfig.integrations) {
    for (const integrationName in sentryConfig.integrations) {
      const integrationConfig = sentryConfig.integrations[integrationName];
      if (integrationConfig !== null) {
        const integration = createIntegration(integrationName, integrationConfig);
        if (integration) {
          integrations.push(integration);
        }
      }
    }
  }

  const finalConfig = {
    release: version,
    merchant: merchant_id,
    ignoreErrors: sentryIgnoreErrors,
    beforeSend: processBeforeSend,
    beforeBreadcrumb: scrubBreadcrumbPII,
    autoSessionTracking: false,
  };

  if(integrations.length) {
    finalConfig.integrations = integrations;
  }

  for (const key in sentryConfig) {
    if (key !== 'integrations') {
      finalConfig[key] = sentryConfig[key];
    }
  }

  return finalConfig;
};

const cleanupSentryConfig = () => {
  const configKeyPrefix = 'SDK_STUDIO_CONFIGS';

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(configKeyPrefix)) {      
      if (key !== `${configKeyPrefix}_${version}`) {
        localStorage.removeItem(key);
      }
    }
  });
};

/**
 * Scrubs sensitive patterns from URLs only
 * @param {string} str - String to scrub
 * @returns {string} String with sensitive patterns replaced if it's a URL
 */
const scrubSensitivePatterns = (str) => {
  if (typeof str !== 'string' || !isUrl(str)) {
    return str;
  }

  let scrubbedStr = str;
  
  // Apply each pattern to scrub sensitive tokens/IDs from URLs only
  for (const pattern of SENSITIVE_URL_PATTERNS) {
    scrubbedStr = scrubbedStr.replace(pattern, '[SCRUBBED_TOKEN]');
  }
  
  return scrubbedStr;
};

/**
 * Recursively scrub sensitive data from objects
 * @param {*} data - Data to scrub
 * @returns {*} Scrubbed data
 */
const scrubSensitiveData = (data) => {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data === 'string') {
    // Apply pattern-based scrubbing to string values
    return scrubSensitivePatterns(data);
  }

  if (Array.isArray(data)) {
    return data.map((item) => scrubSensitiveData(item));
  }

  if (typeof data === 'object') {
    const scrubbed = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();

      // Check if key contains sensitive field names
      // Use word boundaries for short fields to avoid false positives like "currency" -> "enc"
      const isSensitive = SENSITIVE_FIELDS.some((field) => {
        const fieldLower = field.toLowerCase();
        if (fieldLower.length <= 3) {
          // For short field names, require word boundaries to avoid false matches
          const fieldPattern = new RegExp(`\\b${fieldLower}\\b`, 'i');
          return fieldPattern.test(lowerKey);
        } else {
          // For longer field names, allow partial matches
          return lowerKey.includes(fieldLower);
        }
      });

      if (isSensitive) {
        if (Array.isArray(value)) {
          scrubbed[key] = value.map(() => '[SCRUBBED]');
        } else {
          // For sensitive field names, apply URL pattern scrubbing first if it's a URL
          // Otherwise completely scrub the field
          const patternScrubbed = scrubSensitivePatterns(value);
          scrubbed[key] = patternScrubbed !== value ? patternScrubbed : '[SCRUBBED]';
        }
      } else {
        scrubbed[key] = scrubSensitiveData(value);
      }
    }
    return scrubbed;
  }

  return data;
};

/**
 * Scrub PII from breadcrumbs at creation time
 * @param {Object} breadcrumb - Breadcrumb object
 * @returns {Object|null} Scrubbed breadcrumb or null to drop it
 */
const scrubBreadcrumbPII = (breadcrumb) => {
  try {
    const scrubbedCrumb = { ...breadcrumb };

    // Scrub breadcrumb data
    if (scrubbedCrumb.data) {
      scrubbedCrumb.data = scrubSensitiveData(scrubbedCrumb.data);
    }

    // Scrub breadcrumb message
    if (typeof scrubbedCrumb.message === 'string') {
      scrubbedCrumb.message = scrubSensitiveData(scrubbedCrumb.message);
    }

    return scrubbedCrumb;
  } catch (error) {
    console.warn('[Sentry] Breadcrumb PII scrubbing failed:', error);
    return breadcrumb;
  }
};

/**
 * Tags external events that don't originate from the SDK
 * @param {Object} event - Sentry event object
 * @returns {Object} Event with external tag if applicable
 */
const tagExternalEvents = (event) => {
  if(event.exception?.values?.length) {
    const isSDKException = event.exception.values.some((exception)=> 
      exception?.stacktrace?.frames.some((frame)=> 
        frame?.filename?.includes(process.env.SERVER_URL)
      )
    );
    if(!isSDKException) {
      event.tags = event.tags || {};
      event.tags['error_scope'] = 'external';
    }
  }
  return event;
};

/**
 * Scrubs PII from all sensitive areas of a Sentry event
 * @param {Object} event - Sentry event object
 * @returns {Object} Event with PII scrubbed
 */
const scrubEventPII = (event) => {
  try {
    if (event.extra) {
      event.extra = scrubSensitiveData(event.extra);
    }

    if (event.contexts) {
      event.contexts = scrubSensitiveData(event.contexts);
    }

    if (event.user) {
      event.user = scrubSensitiveData(event.user);
    }

    if (event.request) {
      event.request = scrubSensitiveData(event.request);
    }

    // Scrub exception context variables
    if (event.exception?.values) {
      event.exception.values = event.exception.values.map(exception => ({
        ...exception,
        stacktrace: exception.stacktrace ? {
          ...exception.stacktrace,
          frames: exception.stacktrace.frames ? exception.stacktrace.frames.map(frame => ({
            ...frame,
            vars: frame.vars ? scrubSensitiveData(frame.vars) : frame.vars
          })) : exception.stacktrace.frames
        } : exception.stacktrace
      }));
    }

  } catch (scrubError) {
    console.warn('[Sentry] Event PII scrubbing failed:', scrubError);
  }

  return event;
};

/**
 * Combined beforeSend hook that processes events through the pipeline
 * @param {Object} event - Sentry event object
 * @returns {Object|null} Processed event or null to drop it
 */
const processBeforeSend = (event) => {
  // Apply transformations in pipeline
  const taggedEvent = tagExternalEvents(event);
  const scrubbedEvent = scrubEventPII(taggedEvent);
  
  return scrubbedEvent;
};

const setupSentry = async (configs, sentrySetupPreload = {}) => {
  let fetchedConfigs = null;
  let hasPrefetchedConfigs = null;

  if (sentrySetupPreload.sentry) {
    hasPrefetchedConfigs = true;
    fetchedConfigs = sentrySetupPreload;
  } else if (localStorage.getItem(`SDK_STUDIO_CONFIGS_${version}`)) {
    fetchedConfigs = JSON.parse(
      localStorage.getItem(`SDK_STUDIO_CONFIGS_${version}`)
    );
  }

  const shadowRootClosed =
    fetchedConfigs &&
    fetchedConfigs.init &&
    fetchedConfigs.init.shadowRoot === false
      ? false
      : true;
  const captureAll =
    fetchedConfigs &&
    fetchedConfigs.init &&
    fetchedConfigs.init.captureAll === true
      ? true
      : false;
  mainConfigService.updateConfig({ captureAll, shadowRootClosed });

  Sentry.setTag('SDK', 'ottu-js-sdk');
  if(!fetchedConfigs || !fetchedConfigs.sentry) {
    Sentry.init({
      release: version,
      dsn: process.env.DEFAULT_DSN,
      sampleRate: 0.2,
      ignoreErrors: sentryIgnoreErrors,
      beforeSend: processBeforeSend,
      beforeBreadcrumb: scrubBreadcrumbPII,
      autoSessionTracking: false,
    });
  } else {
    const sentryConfigs = createSentryConfig(fetchedConfigs.sentry, configs.merchant_id);
    Sentry.init({
      ...sentryConfigs,
    });
  }

  if (hasPrefetchedConfigs) {
    return;
  }

  const response = await fetchSentryConfig(configs.merchant_id, configs.apiKey);
  if (!response) {
    localStorage.setItem(`SDK_STUDIO_CONFIGS_${version}`, null);
  } else {  
    localStorage.setItem(`SDK_STUDIO_CONFIGS_${version}`, JSON.stringify(response));
  }

  cleanupSentryConfig();
};

/**
 * Captures an exception to Sentry with optional context
 * @param {Error} error - The error object to capture
 * @param {Object} context - Optional context object with additional information
 * @param {string} context.message - Custom message to describe the error context
 * @param {Object} context.extra - Extra data to attach to the error
 * @param {Object} context.tags - Tags to categorize the error
 */
const captureError = (error, context = {}) => {
  try {
    const { message, extra, tags } = context;

    Sentry.withScope((scope) => {
      if (message) {
        scope.setContext('error_context', { description: message });
      }

      if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      Sentry.captureException(error);
    });
  } catch (sentryError) {
    // Fail silently if Sentry capture fails to avoid breaking the application
    console.error('[Sentry] Failed to capture error:', sentryError);
  }
};

exports.setupSentry = setupSentry;
exports.captureError = captureError;
